import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CvFileComponent } from './modules/cv-file/cv-file.component';
import { CvDataComponent } from './modules/cv-data/cv-data.component';
import { SidebarComponent } from './layouts/sidebar/sidebar.component';
import { ContentComponent } from './layouts/content/content.component';
import { CvDetailComponent } from './modules/cv-detail/cv-detail.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SkillDataComponent } from './modules/skill-data/skill-data.component';
import { ProjectDataComponent } from './modules/project-data/project-data.component';
import { MasterDataComponent } from './modules/master-data/master-data.component';
import { ApiModule } from './services/api-client/api.module';

// import ngx-translate and the http loader
import { MissingTranslationHandler, TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgxDocViewerModule } from 'ngx-doc-viewer';
import { ToastrModule } from 'ngx-toastr';
import { ModalModule } from 'ngx-bootstrap/modal';
import { NgMultiSelectDropDownModule } from 'ng-multiselect-dropdown';
import { ProjectDataResolver } from './modules/project-data/project-data.resolver';
import { CvUploadComponent } from './modules/cv-upload/cv-upload.component';
import { RouterModule } from '@angular/router';
import { DefaultTranslationHandler } from './shared/missing-translation-handler';
import { UnSavedChangesGuard } from './guards/un-saved-changes.guard';
import { CvResolver } from './modules/cv-detail/cv.resolver';
import { BranchOfferResolver } from './modules/skill-data/branch-offer.resolver';
import { OAuthModule } from 'angular-oauth2-oidc';
import { CvListComponent } from './modules/cv-list/pages/cv-list.component';
import { CvByIdResolver } from './modules/cv-detail/cvById.resolver';
import { SearchOverviewComponent } from './modules/search-overview/search-overview.component';
import { NgxPaginationModule } from 'ngx-pagination';
import { UserStoreService } from './services/user-store.service';
import { LoginComponent } from './modules/login/login.component';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { QuillModule } from 'ngx-quill';
import { ImageCropperModule } from 'ngx-image-cropper';
import { MatMenuModule } from '@angular/material/menu';
import { CustomSearchInputComponent } from './modules/search-overview/components/custom-search-input/custom-search-input.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { SearchAreaComponent } from './modules/search-overview/components/search-area/search-area.component';
import { NavbarComponent } from './layouts/navbar/navbar.component';
import { MatTabsModule } from '@angular/material/tabs';
import { ScrollAnchorDirective } from './shared/scroll-anchor.directive';
import { ScrollManagerDirective } from './shared/scroll-manager.directive';
import { ScrollSectionDirective } from './shared/scroll-section.directive';
import { CardComponent } from './shared/components/card/card.component';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { QuillMaterialComponent } from './shared/components/quill-material/quill-material.component';
import { MatSliderModule } from '@angular/material/slider';
import { ConfirmationWindowComponent } from './shared/components/confirmation-window/confirmation-window.component';
import { MatDialogModule } from '@angular/material/dialog';
import { MatSidenavModule } from '@angular/material/sidenav';
import { NgxDropzoneModule } from 'ngx-dropzone';
import { ConfirmDeletionPopupComponent } from './shared/components/confirm-deletion-popup/confirm-deletion-popup.component';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule } from '@angular/material/paginator';
import { PaginatorDirective } from './shared/directives/pagination.directive';
import { CvCreateComponent } from './modules/cv-create/cv-create.component';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { MatSortModule } from '@angular/material/sort';
import { MatExpansionModule } from '@angular/material/expansion';
import { environment } from '../environments/environment';
import { TenantInterceptor } from './interceptors/tenant.interceptor';

@NgModule({
  declarations: [
    AppComponent,
    SidebarComponent,
    CvFileComponent,
    CvDataComponent,
    ContentComponent,
    CvDetailComponent,
    MasterDataComponent,
    SkillDataComponent,
    ProjectDataComponent,
    CvUploadComponent,
    CvListComponent,
    SearchOverviewComponent,
    LoginComponent,
    CustomSearchInputComponent,
    SearchAreaComponent,
    NavbarComponent,
    ScrollAnchorDirective,
    ScrollManagerDirective,
    ScrollSectionDirective,
    CardComponent,
    QuillMaterialComponent,
    ConfirmationWindowComponent,
    ConfirmDeletionPopupComponent,
    PaginatorDirective,
    CvCreateComponent
  ],
  imports: [
    ApiModule.forRoot({rootUrl: environment.apiURL}),
    RouterModule,
    BrowserModule,
    BrowserAnimationsModule,
    ModalModule.forRoot(),
    NgMultiSelectDropDownModule.forRoot(),
    // ngx-translate and the loader module
    TranslateModule.forRoot({
      // useDefaultLang: true,
      defaultLanguage: 'de',
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      },
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: DefaultTranslationHandler
      }
    }),
    ReactiveFormsModule,
    HttpClientModule,
    OAuthModule.forRoot({
      resourceServer: {
        allowedUrls: [environment.apiURL],
        sendAccessToken: true
      }
    }),
    FormsModule,
    AppRoutingModule,
    NgxDocViewerModule,
    ToastrModule.forRoot({
      positionClass: 'toast-bottom-right'
    }),
    NgxPaginationModule,
    MatIconModule,
    MatButtonModule,
    MatSnackBarModule,
    BrowserAnimationsModule,
    MatTooltipModule,
    QuillModule.forRoot(),
    ImageCropperModule,
    MatMenuModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatTabsModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatSliderModule,
    MatDialogModule,
    MatSidenavModule,
    NgxDropzoneModule,
    MatTableModule,
    MatPaginatorModule,
    DragDropModule,
    MatSortModule,
    MatExpansionModule
  ],
  providers: [
    ProjectDataResolver,
    BranchOfferResolver,
    CvByIdResolver,
    CvResolver,
    UnSavedChangesGuard,
    UserStoreService,
    { provide: HTTP_INTERCEPTORS, useClass: TenantInterceptor, multi: true },
  ],
  bootstrap: [AppComponent]
})

export class AppModule {

}

// required for AOT compilation
export function HttpLoaderFactory(http: HttpClient): TranslateLoader {
  return new TranslateHttpLoader(http);
}
